import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from '../../toasts';
import { showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { Section, DataTable, IdChip, Pill, RowActionsMenu, Button, ExportModal, buildXlsxExport, exportAmount } from '../../components/EntityList';
import { useClientSort } from '../../hooks/useClientSort';
import { dateValue, parseDueNumber } from '../../utils/tableSort';
import { isCancelledByClose } from '../../utils/dueCancellation';
import ApplySurcharge from './ApplySurcharge';
import ConfirmCountModal from './ConfirmCountModal';
import DebtDueReminderModal from '../../components/DebtDueReminderModal/DebtDueReminderModal';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

// Shared estado rule for debt schedules.
const DUE_ESTADO_TONE = { cancelled: 'neutral', paid: 'ok', pendingCount: 'neutral', overdue: 'danger', pending: 'warn' };
const DUE_ESTADO_LABEL = { cancelled: 'Cancelado', paid: 'Pagada', pendingCount: 'Pendiente conteo', overdue: 'Vencida', pending: 'Pendiente' };

function dueEstado(due) {
	if (isCancelledByClose(due)) return 'cancelled';
	const isPendingCount = due.billingStatus === 'pendingCount';
	if (isPendingCount) return 'pendingCount';
	if (due.paid) return 'paid';
	if (due.expiration && moment.utc(due.expiration).isBefore(moment.utc())) return 'overdue';
	return 'pending';
}

/**
 * Rows are fetched once by the parent (DebtDetails) on page entry and passed
 * down here, rather than self-fetched on mount — this tab used to only fetch
 * when the user actually clicked into it, which showed a 0 count in the tab
 * label and a loading spinner every time the tab was revisited.
 */
function DebtDues({ rows, loading, error, expiredDebt, allDuesGenerated, nextExpirationDate, dueNumber, generationDate, onRefetch }) {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const [showApplySurcharge, setShowApplySurcharge] = useState(false);
	const [selectedDebtDue, setSelectedDebtDue] = useState(null);
	const [showReminderModal, setShowReminderModal] = useState(false);
	const [reminderDue, setReminderDue] = useState(null);
	const [showConfirmCount, setShowConfirmCount] = useState(false);
	const [confirmCountDue, setConfirmCountDue] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'nro',
		accessors: {
			nro: (due) => parseDueNumber(due.customId),
			id: (due) => due.customId,
			date: (due) => dateValue(due.date),
			expiration: (due) => dateValue(due.expiration),
		},
	});

	function NewDueHandler() {
		navigate(`/AdminScreen/debts/debt/${id}/newDue`);
	}

	function applySurchargeHandler(debtDue) {
		setSelectedDebtDue(debtDue);
		setShowApplySurcharge(true);
	}

	function addPaymentHandler(debtDue) {
		if (debtDue.paid) {
			toast.info(t('msg.dueAlreadyPaid'));
			return;
		}
		if (debtDue.billingStatus === 'pendingCount') {
			toast.info(t('msg.confirmCountBeforePayment'));
			return;
		}
		if (isCancelledByClose(debtDue)) {
			toast.info(t('msg.dueCancelledByClose'));
			return;
		}
		navigate(`/AdminScreen/debts/debt/${id}/dues/${debtDue._id}/newPayment`);
	}

	function confirmCountHandler(debtDue) {
		setConfirmCountDue(debtDue);
		setShowConfirmCount(true);
	}

	function sendReminderHandler(debtDue) {
		setReminderDue(debtDue);
		setShowReminderModal(true);
	}

	const progressMessage = expiredDebt
		? t('msg.debtExpired')
		: allDuesGenerated
			? t('msg.allDuesGenerated')
			: t('msg.nextExpirationTemplate')
				.replace('{date}', nextExpirationDate)
				.replace('{number}', dueNumber)
				.replace('{generationDate}', generationDate);

	const columns = [
		{
			key: 'nro',
			header: 'Nro',
			sortable: true,
			className: 'tw:font-mono tw:tabular-nums',
			render: (due) => parseDueNumber(due.customId) ?? <span className='tw:text-ink-3'>—</span>,
		},
		{ key: 'id', header: 'ID', sortable: true, render: (due) => <IdChip>{due.customId}</IdChip> },
		{ key: 'date', header: t('field.date'), sortable: true, render: (due) => formatDate(due.date) },
		{ key: 'expiration', header: t('field.expiration'), sortable: true, render: (due) => formatDate(due.expiration) },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (due) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(due.debt?.currency, due.amount)}</span>,
		},
		{
			key: 'unpaidAmount',
			header: t('field.unpaidAmount'),
			className: 'tw:text-right',
			render: (due) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(due.debt?.currency, due.unpaidAmount)}</span>,
		},
		{
			key: 'estado',
			header: t('field.status'),
			render: (due) => {
				const estado = dueEstado(due);
				return <Pill tone={DUE_ESTADO_TONE[estado]}>{DUE_ESTADO_LABEL[estado]}</Pill>;
			},
		},
	];

	const exportFields = [
		{ key: 'nro', label: 'Nro' },
		{ key: 'id', label: 'ID', locked: true },
		{ key: 'date', label: t('field.date') },
		{ key: 'expiration', label: t('field.expiration') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'unpaidAmount', label: t('field.unpaidAmount') },
		{ key: 'estado', label: t('field.status') },
	];

	const exportGetters = {
		nro: (due) => parseDueNumber(due.customId) ?? '',
		id: (due) => due.customId || '',
		date: (due) => formatDate(due.date),
		expiration: (due) => formatDate(due.expiration),
		currency: (due) => due.debt?.currency || '',
		amount: (due) => exportAmount(due.amount),
		unpaidAmount: (due) => exportAmount(due.unpaidAmount),
		estado: (due) => DUE_ESTADO_LABEL[dueEstado(due)] || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Cuotas',
			filePrefix: 'cuotas',
		});

	const renderRowActions = (due) => (
		<RowActionsMenu
			items={[
				{ label: t('action.applySurcharge'), onClick: () => applySurchargeHandler(due) },
				{ label: 'Agregar cobro', onClick: () => addPaymentHandler(due) },
				due.billingStatus === 'pendingCount' && { label: t('title.confirmUserCount'), onClick: () => confirmCountHandler(due) },
				{ label: 'Enviar recordatorio', onClick: () => sendReminderHandler(due) },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<div className='tw:flex tw:items-center tw:justify-between tw:gap-3'>
				<span className='tw:text-[12.5px] tw:text-ink-3'>{progressMessage}</span>
				<div className='tw:flex tw:gap-2'>
					<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
						{t('action.export')}
					</Button>
					<Button size='sm' onClick={NewDueHandler}>
						Agregar cuota manual
					</Button>
				</div>
			</div>
			<Section title={t('entity.due.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron cuotas para esta deuda.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle='Cuotas de esta deuda'
				onExport={handleExport}
			/>

			<ApplySurcharge
				debtDue={selectedDebtDue}
				show={showApplySurcharge}
				onClose={() => {
					setShowApplySurcharge(false);
					setSelectedDebtDue(null);
				}}
				onSaved={onRefetch}
			/>
			<DebtDueReminderModal
				show={showReminderModal}
				onHide={() => {
					setShowReminderModal(false);
					setReminderDue(null);
				}}
				due={reminderDue}
			/>
			<ConfirmCountModal
				show={showConfirmCount}
				onHide={() => {
					setShowConfirmCount(false);
					setConfirmCountDue(null);
				}}
				due={confirmCountDue}
				onSaved={onRefetch}
			/>
		</div>
	);
}

export default DebtDues;
